import { useEffect, useMemo, useState } from 'react';
import { LinkButton, PrimaryButton, SearchField, SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { useItems } from 'src/hooks/useItems/useItems';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { useItemsSelection } from 'src/hooks/useItemsSelection';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { addItemsToMenu } from 'src/api/addItemsToMenu';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { ItemType } from 'src/constants';
import { Range } from '@weareopenr/types';
import SelectableListWithControls from 'src/components/SelectableListWithControls';
import { toMap, getRangeSubtitle } from 'src/helpers';
import { boldMenuName } from 'src/helpers/truncateMenuName/boldMenuName';
import styles from './AddProductsToMenuTrayContent.module.scss';

const TRAY_CLOSE_ANIMATION_MS = 450;

const CREATE_NEW_PRODUCT_LABEL = 'Create New Product';
const PRODUCTS_LIST_TITLE = 'Products:';
const SEARCH_PLACEHOLDER = 'Search products...';
const CANCEL_LABEL = 'Cancel';
const SAVE_LABEL = 'Save';
const GENERIC_ERROR_MESSAGE = 'Failed to add products to menu';

export interface AddProductsToMenuTrayContentProps {
  menuName: string;
  menuId: string;
  sectionId: string | null;
  onCreateNewProduct: () => void;
  menuRanges: Range[];
  onClose: () => void;
  openId: number;
  onSavingChange?: (count: number) => void;
}

export const AddProductsToMenuTrayContent = ({
  menuName,
  menuId,
  sectionId,
  onCreateNewProduct,
  menuRanges = [],
  onClose,
  openId,
  onSavingChange,
}: AddProductsToMenuTrayContentProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const menuRangeIds = useMemo(() => menuRanges.map((range) => range.range_id), [menuRanges]);

  const debouncedSearchTerm = useDebounce(searchTerm);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useItems(
    debouncedSearchTerm,
    50,
    [ItemType.BUNDLE, ItemType.STANDARD, ItemType.NON_FOOD],
    undefined,
    menuRangeIds.length ? menuRangeIds : undefined,
  );

  const productOptions = useMemo(
    () =>
      data?.pages?.flatMap((page) =>
        page.map((item) => ({
          id: item.itemId,
          label: item.name,
          subtitle: getRangeSubtitle(item.ranges),
        })),
      ) ?? [],
    [data],
  );

  const productOptionsMap = useMemo(() => toMap(productOptions, 'id'), [productOptions]);

  const {
    selectedIds: selectedProducts,
    selectedItems: selectedProductOptions,
    handleSelectionChange,
    reset: resetSelection,
  } = useItemsSelection(productOptionsMap);

  useEffect(() => {
    resetSelection();
    setSearchTerm('');
  }, [openId]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    const totalAdded = selectedProducts.length;
    const requestBody = selectedProducts.map((itemId) => ({
      item_id: itemId,
      section_id: sectionId ?? null,
    }));

    onClose();
    let hasSettled = false;
    const showShimmerTimeout = setTimeout(() => {
      if (!hasSettled) onSavingChange?.(totalAdded);
    }, TRAY_CLOSE_ANIMATION_MS);

    try {
      await addItemsToMenu(menuId, requestBody);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });

      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: (
          <>
            {`Successfully added ${totalAdded} product${totalAdded > 1 ? 's' : ''} to `}
            {boldMenuName(menuName)}
          </>
        ),
      });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: error instanceof Error ? error.message : GENERIC_ERROR_MESSAGE,
      });
    } finally {
      hasSettled = true;
      clearTimeout(showShimmerTimeout);
      setIsSubmitting(false);
      onSavingChange?.(0);
    }
  };

  return (
    <div className={styles['content']}>
      <SelectableListWithControls
        className={styles['list']}
        options={productOptions}
        selectedOptionsIds={selectedProducts}
        onOptionChange={handleSelectionChange}
        scrollContainerClassName={styles['scroll-container']}
        collapsible={{ selectedOptionsData: selectedProductOptions }}
        listAction={{
          label: CREATE_NEW_PRODUCT_LABEL,
          onClick: () => {
            onClose();
            onCreateNewProduct();
          },
        }}
        listDetails={{ title: PRODUCTS_LIST_TITLE, isLoading }}
        pagination={{ hasNextPage, onLoadMore: fetchNextPage, isLoadingMore: isFetchingNextPage }}
      >
        <SearchField
          key={openId}
          className={styles['search-field']}
          placeholder={SEARCH_PLACEHOLDER}
          onChange={(e) => setSearchTerm(e.target.value)}
          isDisabled={isSubmitting}
        />
      </SelectableListWithControls>

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <LinkButton type="button" onClick={onClose}>
              {CANCEL_LABEL}
            </LinkButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0 || isSubmitting}
            >
              {SAVE_LABEL}
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export default AddProductsToMenuTrayContent;
